/**
 * Access Control Resolver
 * Pure function to determine if a user with given roles can access a feature slug.
 * Consumed by both useAccess hook and RequireAccess component.
 *
 * GrowthBook feature: "emd-access-control"
 * Shape: { "slug": { "allowed": ["ROLE_ADMIN", ...], "denied": ["ROLE_USER"] }, ... }
 *
 * Rules:
 *  1. If the slug is not found, access is DENIED (fail-closed).
 *  2. "denied" takes precedence over "allowed".
 *  3. A wildcard "*" in "allowed" means every role is allowed (unless denied).
 */

export interface SlugRule {
  allowed: string[]
  denied: string[]
}

export type AccessControlMap = Record<string, SlugRule>

/**
 * Resolve whether the given user roles grant access to a slug.
 * @param slug   - Feature/component identifier, e.g. "page:ai-productivity"
 * @param userRoles - Array of role strings from the JWT, e.g. ["ROLE_ADMIN"]
 * @param acl    - The full access-control map from GrowthBook
 * @returns true if access is granted, false otherwise
 */
export const canAccess = (slug: string, userRoles: string[], acl: AccessControlMap): boolean => {
  const rule = acl[slug]

  // Slug not configured → fail-closed
  if (!rule) return false

  // Denied takes precedence
  if (rule.denied?.length > 0 && userRoles.some(role => rule.denied.includes(role))) {
    return false
  }

  // Check allowed list — wildcard "*" means all roles
  if (rule.allowed?.includes('*')) return true

  return rule.allowed?.length > 0 && userRoles.some(role => rule.allowed.includes(role))
}
